// THE GROUND GRADIENT — one gradient, painted once, masked everywhere.
//
// The ground used to be a solid `--bg` plus a single top→base wash, and every
// piece of frosted chrome (the navigator band, the corner veils, the dock veil)
// re-mixed its OWN tint out of `--bg`/`--bg-top`. That meant N gradients, each
// living in its own element's coordinate space, each easing on its own clock —
// so dragging the dial made the navigator's mask visibly lag the ground beneath
// it, and the two never quite agreed on a colour.
//
// Now there is exactly ONE gradient. Its four stops live on :root as registered
// `<color>` properties, so:
//   · every surface that wants the ground reads the SAME `--ground-grad`;
//   · each surface paints it FULL-VIEWPORT and masks it to its own shape, so a
//     corner veil samples the identical colour the ground has at that corner;
//   · a change interpolates once, at the root, and every layer moves as one —
//     nothing can lag, because there is nothing separate to lag behind.
//
// The bender (the dial's innermost ring, 0..1) is the only input beyond the
// ground colour itself: it opens the hue fan and lifts the intensity together.

export const GRAD_KEY = "innernet:ground-grad";

/** below this the ground is solid — the stops go fully transparent. */
const SOLID = 0.006;

const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);

// HSV → RGB, kept local so this module stays a leaf: lib/theme.ts calls it on
// every ground change, and theme.ts must not drag a client component into the
// server graph just to convert a colour.
function hsvToRgb01(h: number, s: number, v: number): [number, number, number] {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  const [r, g, b] =
    h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x]
    : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return [r + m, g + m, b + m];
}

/** RGB (0..1) → HSV, the inverse of the above — same maths as ColorInstrument. */
export function rgbToHsv01(c: [number, number, number] | readonly [number, number, number]) {
  const [r, g, b] = c;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === r) h = 60 * (((g - b) / d) % 6);
    else if (max === g) h = 60 * ((b - r) / d + 2);
    else h = 60 * ((r - g) / d + 4);
  }
  if (h < 0) h += 360;
  return { h, s: max === 0 ? 0 : d / max, v: max };
}

function rgba(h: number, s: number, v: number, a: number): string {
  const c = hsvToRgb01(((h % 360) + 360) % 360, clamp01(s), clamp01(v));
  return `rgba(${Math.round(c[0] * 255)}, ${Math.round(c[1] * 255)}, ${Math.round(c[2] * 255)}, ${a.toFixed(3)})`;
}

/**
 * The four stops of the one ground gradient, derived from the picked ground
 * (HSV) and the bender level `g`.
 *
 * Three washes and a lift, all rotating AWAY from the base hue in different
 * directions as `g` opens — so the ground gains depth and a hue drift together
 * rather than just getting lighter at the top:
 *   · a — the shoulder glow, top-left: hue runs forward, brightness lifts
 *   · b — the counter-light, top-right: hue runs back, cooler
 *   · c — the settle, bottom: hue runs slightly forward, brightness sinks
 *   · top — the vertical lift the whole page reads as "light comes from above"
 */
export function groundStops(h: number, s: number, v: number, g: number) {
  const t = clamp01(g);
  if (t < SOLID) {
    return { a: "transparent", b: "transparent", c: "transparent", top: "transparent" };
  }
  return {
    a: rgba(h + t * 42, s * (1 - t * 0.24), clamp01(v + t * 0.15), 0.62 * t),
    b: rgba(h - t * 30, s * (1 - t * 0.08), clamp01(v + t * 0.06), 0.4 * t),
    c: rgba(h + t * 16, clamp01(s * (1 + t * 0.2)), clamp01(v - t * 0.12), 0.5 * t),
    top: rgba(h + t * 32, s * (1 - t * 0.16), clamp01(v + t * 0.09), 0.52 * t),
  };
}

/**
 * Paint the stops onto :root — or onto ANOTHER document's root (`into`), which
 * is how the share studio tints the live page it is previewing inside its
 * frame: one ground, painted by the one function, wherever it has to land.
 * Cheap enough for the hot path — four custom property writes, no string
 * building of the gradient itself (that lives in globals.css as
 * `--ground-grad` and never changes).
 */
export function applyGroundGradient(h: number, s: number, v: number, g: number, into?: HTMLElement | null): void {
  if (typeof document === "undefined") return;
  const el = into ?? document.documentElement;
  if (!el) return;
  const { a, b, c, top } = groundStops(h, s, v, g);
  const root = el.style;
  root.setProperty("--g-a", a);
  root.setProperty("--g-b", b);
  root.setProperty("--g-c", c);
  root.setProperty("--g-top", top);
}

/** the saved bender level (0 when never set / unreadable). */
export function readGradLevel(): number {
  try {
    const raw = Number(localStorage.getItem(GRAD_KEY));
    return Number.isFinite(raw) ? clamp01(raw) : 0;
  } catch {
    return 0;
  }
}

export function saveGradLevel(g: number): void {
  try {
    localStorage.setItem(GRAD_KEY, g.toFixed(3));
  } catch {
    /* private mode */
  }
}

/**
 * While a knob is being dragged the ground must track the finger EXACTLY — no
 * eased settle on any layer, or the ground and its masks drift apart mid-drag.
 * On release the class comes off and the shared settle takes over again.
 */
export function setGroundLive(live: boolean): void {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("ground-live", live);
}
